export type ReminderType = "remaining_2" | "expires_14" | "expires_7" | "expires_1" | "expired" | "used_up";

export interface PassEligibilityFields {
  status: string;
  start_date: string;
  pass_type: string;
  remaining_classes: number | null;
  activated_at?: string | null;
  expiry_date: string | null;
}

interface PassChoiceFields extends PassEligibilityFields {
  pass_id: string;
  created_at?: string;
}

export function schoolDate(timeZone: string, at = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  return map.year + "-" + map.month + "-" + map.day;
}

export function schoolHour(timeZone: string, at = new Date()): number {
  return Number(new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    hourCycle: "h23",
  }).format(at));
}

export function addDays(dateIso: string, days: number): string {
  const [year, month, day] = dateIso.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  const start = Date.parse(from + "T00:00:00Z");
  const end = Date.parse(to + "T00:00:00Z");
  return Math.round((end - start) / 86_400_000);
}

export function isPassEligibleOn(pass: PassEligibilityFields, schoolDateIso: string): boolean {
  return pass.status === "active"
    && pass.start_date <= schoolDateIso
    && (pass.pass_type === "unlimited" || Number(pass.remaining_classes) > 0)
    && (pass.activated_at === null || !pass.expiry_date || schoolDateIso <= pass.expiry_date);
}

export function passEligibilitySql(alias = "passes"): string {
  const prefix = alias ? `${alias}.` : "";
  return `${prefix}status = 'active' AND ${prefix}start_date <= ?
    AND (${prefix}pass_type = 'unlimited' OR ${prefix}remaining_classes > 0)
    AND (${prefix}activated_at IS NULL OR ${prefix}expiry_date IS NULL OR ${prefix}expiry_date >= ?)`;
}

export function passExpiredBeforeSql(alias = "passes"): string {
  const prefix = alias ? `${alias}.` : "";
  return `${prefix}status = 'active' AND ${prefix}expiry_date IS NOT NULL AND ${prefix}expiry_date < ?`;
}

export function choosePass<T extends PassChoiceFields>(passes: T[], today: string): T | null {
  const usable = passes.filter((pass) => isPassEligibleOn(pass, today));
  const activated = usable.filter((pass) => pass.activated_at !== null);
  const unlimited = activated
    .filter((pass) => pass.pass_type === "unlimited")
    .sort((a, b) => (a.expiry_date ?? "9999-12-31").localeCompare(b.expiry_date ?? "9999-12-31"));
  if (unlimited[0]) return unlimited[0];
  const countPass = activated
    .sort((a, b) => (a.expiry_date ?? "9999-12-31").localeCompare(b.expiry_date ?? "9999-12-31"))[0] ?? null;
  if (countPass) return countPass;
  return usable.filter((pass) => pass.activated_at === null)
    .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? "") || a.pass_id.localeCompare(b.pass_id))[0] ?? null;
}

export const DEFAULT_PASS_VALIDITY_DAYS = 30;

export function passUsableThroughDate(activationDate: string, validityDays: number, extensionDays = 0): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(activationDate)) throw new Error("Invalid pass activation date");
  if (!Number.isInteger(validityDays) || validityDays <= 0) throw new Error("Pass validity must be a positive whole number of days");
  if (!Number.isInteger(extensionDays) || extensionDays < 0) throw new Error("Pass extension must be a non-negative whole number of days");
  return addDays(activationDate, validityDays - 1 + extensionDays);
}

export function existingPassExtensionDays(activatedAt: string | null | undefined, expiryDate: string | null,
  validityDays: number, timeZone: string): number {
  if (!activatedAt || !expiryDate) return 0;
  const activationDate = schoolDate(timeZone, new Date(activatedAt));
  const baseExpiry = passUsableThroughDate(activationDate, validityDays);
  return Math.max(0, daysBetween(baseExpiry, expiryDate));
}

export function passActivationExpiry(checkedInAt: string, timeZone: string, validityDays: number): string {
  return passUsableThroughDate(schoolDate(timeZone, new Date(checkedInAt)), validityDays);
}

export function passExtendedExpiry(lastUsableDate: string, extensionDays: number): string {
  if (!Number.isInteger(extensionDays) || extensionDays <= 0) throw new Error("Pass extension must be a positive whole number of days");
  return addDays(lastUsableDate, extensionDays);
}

export type PassLifecycleStatus = "not_activated" | "active" | "expired" | "used_up" | "cancelled";

export function passLifecycleStatus(pass: PassEligibilityFields, today: string): PassLifecycleStatus {
  if (pass.status === "cancelled") return "cancelled";
  if (pass.activated_at === null) return "not_activated";
  if (pass.status === "expired" || (pass.expiry_date && today > pass.expiry_date)) return "expired";
  if (pass.status === "used_up" || (pass.pass_type === "count" && Number(pass.remaining_classes) <= 0)) return "used_up";
  return "active";
}

export function reminderForPass(pass: PassEligibilityFields, today: string): ReminderType | null {
  const lifecycle = passLifecycleStatus(pass, today);
  if (lifecycle === "not_activated" || lifecycle === "cancelled") return null;
  if (lifecycle === "used_up") return "used_up";
  if (lifecycle === "expired") return "expired";
  if (pass.expiry_date) {
    const days = daysBetween(today, pass.expiry_date);
    if (days === 1) return "expires_1";
    if (days === 7) return "expires_7";
    if (days === 14) return "expires_14";
  }
  if (pass.pass_type === "count" && Number(pass.remaining_classes) <= 2) return "remaining_2";
  return null;
}
